import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { SeriesBadge } from "@/components/SeriesBadge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getSeriesProducts } from "@/data/series-products";
import { trackBadgeClick, trackSeriesCta, trackSeriesProductClick } from "@/lib/analytics";

export interface SeriesDrilldownDialogProps {
  /** Series slug to show; `null` keeps the dialog closed. */
  slug: string | null;
  onClose: () => void;
  /** Analytics placement of the badge grid that opened this dialog. */
  placement: string;
}

/**
 * Badge drill-down modal — shows every product that lives under a Signature
 * Collection badge with a direct link to each product page, plus a CTA into the
 * full series landing page. Opened from any badge grid so a visitor can see
 * exactly what a badge covers before committing to a click.
 */
export function SeriesDrilldownDialog({ slug, onClose, placement }: SeriesDrilldownDialogProps) {
  const entry = slug ? getSeriesProducts(slug) : undefined;
  const drilldownPlacement = `${placement}_drilldown`;

  return (
    <Dialog
      open={Boolean(entry)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="max-h-[88vh] max-w-3xl overflow-y-auto border-white/10 bg-navy-deep p-0 text-white [&>button]:z-10 [&>button]:text-white/80 [&>button:hover]:text-red"
      >
        {entry ? (
          <>
            <DialogHeader className="relative space-y-0 border-b border-white/10 p-6 text-left">
              <div className="flex flex-col items-start gap-4 pr-10 sm:flex-row sm:items-center">
                <span className="flex h-24 w-24 shrink-0 items-center justify-center">
                  <SeriesBadge slug={entry.slug} size="sm" className="max-h-full w-auto" />
                </span>
                <div>
                  <div className="font-display text-[11px] uppercase tracking-[0.25em] text-red">
                    {entry.market}
                  </div>
                  <DialogTitle className="mt-1 font-display text-2xl font-bold uppercase leading-tight text-white md:text-3xl">
                    {entry.fullName}
                  </DialogTitle>
                  <DialogDescription className="mt-2 text-sm leading-snug text-white/80">
                    {entry.audience}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="p-6">
              <h3 className="font-display text-xs uppercase tracking-[0.25em] text-white/70">
                What's under this badge — {entry.products.length} products &amp; services
              </h3>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {entry.products.map((p) => (
                  <li key={`${p.label}-${p.to}`} className="flex">
                    <Link
                      to={p.to}
                      onClick={() => {
                        trackSeriesProductClick(
                          { slug: entry.slug, name: entry.name, placement: drilldownPlacement },
                          p.to,
                          p.label,
                        );
                        onClose();
                      }}
                      className="group flex w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:border-red/60 hover:bg-white/10 hover:text-red"
                    >
                      <span className="leading-snug">{p.label}</span>
                      <ArrowRight
                        className="h-4 w-4 shrink-0 text-white/75 transition group-hover:translate-x-0.5 group-hover:text-red"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={entry.path}
                  onClick={() => {
                    trackBadgeClick(
                      { slug: entry.slug, name: entry.name, placement: drilldownPlacement },
                      entry.path,
                    );
                    onClose();
                  }}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-red px-5 py-3 font-display text-sm font-bold uppercase tracking-wide text-white transition hover:bg-red/90"
                >
                  Explore {entry.fullName}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/estimate-quotes"
                  onClick={() => {
                    trackSeriesCta(
                      { slug: entry.slug, name: entry.name, placement: drilldownPlacement },
                      "/estimate-quotes",
                      "Request a Quote",
                    );
                    onClose();
                  }}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-md border border-white/25 px-5 py-3 font-display text-sm font-bold uppercase tracking-wide text-white transition hover:border-red hover:text-red"
                >
                  Request a Quote
                </Link>
              </div>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
